class ImageTextCarousel extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.slideWidth = 0;
    this.dragging = false;
    this.startX = 0;
    this.deltaX = 0;
    this.autoplayTimer = null;
    this.transitionMs = 400;

    this.handleResize = this.handleResize.bind(this);
    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerUp = this.handlePointerUp.bind(this);
  }

  connectedCallback() {
    // 找到轨道和所有 slide
    this.track = this.querySelector("[data-track]");
    if (!this.track) return;

    this.slides = Array.from(this.track.querySelectorAll("[data-slide]"));
    if (!this.slides.length) return;

    // 读取 autoplay 配置（来自 liquid 的属性）
    this.autoplayEnabled = this.getAttribute("autoplay") === "true";
    const intervalAttr = parseInt(this.getAttribute("interval"), 10);
    this.autoplayInterval = isNaN(intervalAttr) ? 5000 : intervalAttr;

    // 基础样式
    this.track.style.display = "flex";
    this.track.style.willChange = "transform";
    this.track.style.transition = `transform ${this.transitionMs}ms ease-in-out`;

    this.slides.forEach((slide) => {
      slide.style.flexShrink = "0";
    });

    // 初始尺寸 & 事件绑定
    this.handleResize();
    window.addEventListener("resize", this.handleResize, { passive: true });

    this.track.addEventListener("pointerdown", this.handlePointerDown);
    this.track.addEventListener("pointermove", this.handlePointerMove);
    this.track.addEventListener("pointerup", this.handlePointerUp);
    this.track.addEventListener("pointerleave", this.handlePointerUp);
    this.track.addEventListener("pointercancel", this.handlePointerUp);

    // hover / touch 时暂停自动播放，离开时恢复
    this.addEventListener("touchstart", () => this.stopAutoplay(), { passive: true });
    this.addEventListener("touchend", () => this.startAutoplay(), { passive: true });
    this.addEventListener("mouseenter", () => this.stopAutoplay());
    this.addEventListener("mouseleave", () => this.startAutoplay());

    this.updateTransform(true);
    this.startAutoplay();
  }

  disconnectedCallback() {
  window.removeEventListener("resize", this.handleResize);
  if (this.track) {
    this.track.removeEventListener("pointerdown", this.handlePointerDown);
    this.track.removeEventListener("pointermove", this.handlePointerMove);
    this.track.removeEventListener("pointerup", this.handlePointerUp);
    this.track.removeEventListener("pointerleave", this.handlePointerUp);
    this.track.removeEventListener("pointercancel", this.handlePointerUp);
  }
  this.stopAutoplay();
}


    // 根据组件宽度设置每一页宽度（锁死一屏一张）
  handleResize() {
    const rect = this.getBoundingClientRect();
    this.slideWidth = rect.width || 0;
    if (!this.slideWidth) return;

    this.slides.forEach((slide) => {
      // 🔒 每个 slide 的实际宽度 = 轮播组件宽度
      slide.style.width = `${this.slideWidth}px`;
      slide.style.minWidth = `${this.slideWidth}px`;
      slide.style.maxWidth = `${this.slideWidth}px`;
      // 避免 flex 把 item 撑得比容器还宽
      slide.style.flex = `0 0 ${this.slideWidth}px`;
    });

    // 按当前 index 重新对齐轨道
    this.updateTransform(true);
  }

  // 更新位移
  updateTransform(instant = false) {
    if (!this.track) return;
    if (instant) {
      this.track.style.transition = "none";
    } else {
      this.track.style.transition = `transform ${this.transitionMs}ms ease-in-out`;
    }
    const offset = -this.currentIndex * this.slideWidth;
    this.track.style.transform = `translateX(${offset}px)`;

    // 瞬移之后，下一帧恢复过渡
    if (instant) {
      requestAnimationFrame(() => {
        this.track.style.transition = `transform ${this.transitionMs}ms ease-in-out`;
      });
    }
  }

  // 开始拖动
  handlePointerDown(event) {
    // 只响应左键
    if (event.button !== undefined && event.button !== 0) return;
    this.stopAutoplay();
    this.dragging = true;
    this.startX = event.clientX || (event.touches && event.touches[0]?.clientX) || 0;
    this.deltaX = 0;
    this.track.style.transition = "none";
    this.setAttribute("data-dragging", "true");
  }

  // 拖动中
  handlePointerMove(event) {
    if (!this.dragging) return;
    const currentX = event.clientX || (event.touches && event.touches[0]?.clientX) || 0;
    this.deltaX = currentX - this.startX;
    const offset = -this.currentIndex * this.slideWidth + this.deltaX;
    this.track.style.transform = `translateX(${offset}px)`;
  }

  // 拖动结束
  handlePointerUp() {
    if (!this.dragging) return;
    this.dragging = false;
    this.removeAttribute("data-dragging");

    const threshold = this.slideWidth * 0.15;

    // 向右拖：上一张（非循环，不能小于 0）
    if (this.deltaX > threshold && this.currentIndex > 0) {
      this.currentIndex -= 1;
    }
    // 向左拖：下一张（非循环，不能超过最后一张）
    else if (this.deltaX < -threshold && this.currentIndex < this.slides.length - 1) {
      this.currentIndex += 1;
    }

    this.updateTransform(false);
    this.deltaX = 0;
    this.startAutoplay();
  }

  // 自动播放：从 0 播到最后一张，然后瞬间切回第一张继续循环
  startAutoplay() {
    if (!this.autoplayEnabled) return;
    if (this.autoplayTimer) return;

    // 尊重系统减少动效设置
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    this.autoplayTimer = window.setInterval(() => {
      if (this.dragging) return;
      if (!this.slides || !this.slides.length || !this.slideWidth) return;

      if (this.currentIndex < this.slides.length - 1) {
        // 正常向后滑
        this.currentIndex += 1;
        this.updateTransform(false);
      } else {
        // 到最后一张后，瞬间切回第一张（无长距离动画），实现循环
        this.currentIndex = 0;
        this.updateTransform(true);
      }
    }, this.autoplayInterval);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }
}

customElements.define("image-text-carousel", ImageTextCarousel);
